import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { AuthPageShell } from '@/components/app/auth-page-shell';
import { ApiError, resetPasswordRequest } from '@/lib/api';

const schema = z
  .object({
    password: z.string().min(8, 'resetPassword.passwordTooShort'),
    confirm: z.string().min(1, 'resetPassword.confirmRequired'),
  })
  .refine((v) => v.password === v.confirm, {
    path: ['confirm'],
    message: 'resetPassword.confirmMismatch',
  });

type FormValues = z.infer<typeof schema>;

function PasswordInput({
  id,
  label,
  placeholder,
  error,
  disabled,
  registration,
}: {
  id: string;
  label: string;
  placeholder: string;
  error?: string | undefined;
  disabled: boolean;
  registration: Record<string, unknown>;
}) {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input
          id={id}
          type={show ? 'text' : 'password'}
          autoComplete="new-password"
          placeholder={placeholder}
          disabled={disabled}
          className="h-11 pr-11"
          {...registration}
        />
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          aria-label={t(show ? 'resetPassword.hidePassword' : 'resetPassword.showPassword')}
          aria-pressed={show}
          className="absolute right-1.5 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-[7px] text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {show ? (
            <EyeOff className="h-4 w-4" aria-hidden="true" />
          ) : (
            <Eye className="h-4 w-4" aria-hidden="true" />
          )}
        </button>
      </div>
      {error && (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function ErrorState({ titleKey, bodyKey }: { titleKey: string; bodyKey: string }) {
  const { t } = useTranslation();
  return (
    <div className="mt-9">
      <div
        className="flex items-start gap-3 rounded-[9px] border border-destructive/30 bg-destructive/10 px-4 py-4"
        role="alert"
      >
        <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" aria-hidden="true" />
        <div>
          <h1 className="text-[17px] font-semibold leading-snug text-foreground">{t(titleKey)}</h1>
          <p className="mt-1.5 text-sm leading-snug text-muted-foreground">{t(bodyKey)}</p>
        </div>
      </div>
      <div className="mt-6 flex flex-col items-center gap-3 text-sm">
        <Button asChild className="h-11 w-full">
          <Link to="/forgot-password">{t('resetPassword.requestNewLink')}</Link>
        </Button>
        <Link
          to="/login"
          className="rounded-[4px] font-medium text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {t('forgotPassword.backToLogin')}
        </Link>
      </div>
    </div>
  );
}

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const [tokenInvalid, setTokenInvalid] = useState(false);

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  if (!token) {
    return (
      <AuthPageShell>
        <ErrorState
          titleKey="resetPassword.missingTokenTitle"
          bodyKey="resetPassword.missingTokenBody"
        />
      </AuthPageShell>
    );
  }

  if (tokenInvalid) {
    return (
      <AuthPageShell>
        <ErrorState
          titleKey="resetPassword.invalidTokenTitle"
          bodyKey="resetPassword.invalidTokenBody"
        />
      </AuthPageShell>
    );
  }

  const onSubmit = async (data: FormValues) => {
    clearErrors('root');
    try {
      await resetPasswordRequest(token, data.password);
      toast.success(t('resetPassword.successToast'));
      navigate('/login', { replace: true });
    } catch (error) {
      if (error instanceof ApiError && error.status === 400) {
        setTokenInvalid(true);
      } else if (error instanceof ApiError && error.status === 429) {
        setError('root', { message: t('resetPassword.rateLimited') });
      } else {
        toast.error(t('resetPassword.unreachable'));
      }
    }
  };

  return (
    <AuthPageShell>
      <h1 className="mt-9 text-[26px] font-semibold leading-tight tracking-tight text-foreground">
        {t('resetPassword.title')}
      </h1>
      <p className="mt-2 font-serif text-[16px] italic leading-snug text-muted-foreground">
        {t('resetPassword.subtitle')}
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-5" noValidate>
        <PasswordInput
          id="password"
          label={t('resetPassword.password')}
          placeholder={t('resetPassword.passwordPlaceholder')}
          disabled={isSubmitting}
          error={errors.password ? t(String(errors.password.message)) : undefined}
          registration={register('password')}
        />
        <PasswordInput
          id="confirm"
          label={t('resetPassword.confirm')}
          placeholder={t('resetPassword.confirmPlaceholder')}
          disabled={isSubmitting}
          error={errors.confirm ? t(String(errors.confirm.message)) : undefined}
          registration={register('confirm')}
        />

        {errors.root && (
          <div
            className="flex items-start gap-2.5 rounded-[9px] border border-destructive/30 bg-destructive/10 px-3.5 py-3"
            role="alert"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
            <p className="text-sm leading-snug text-foreground">{errors.root.message}</p>
          </div>
        )}

        <Button type="submit" className="h-11 w-full" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              {t('resetPassword.submitting')}
            </>
          ) : (
            t('resetPassword.submit')
          )}
        </Button>
      </form>
    </AuthPageShell>
  );
}
